import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { type LensCardState, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { type SvgIconComponent } from '@mui/icons-material';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BarChartIcon from '@mui/icons-material/BarChart';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import GridOnIcon from '@mui/icons-material/GridOn';
import InsightsIcon from '@mui/icons-material/Insights';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MultilineChartIcon from '@mui/icons-material/MultilineChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';

import { BRAND_COLORS } from '~/components/brand';

import LeDataMarkdownPreview from '~/pages/AskRichardUnified/chat/LeDataMarkdownPreview';

import { streamReportingAiAsk } from '~/services/ReportingAiAskService';

import type {
  LensCard,
  LensCardAlignment,
  LensCardData,
  LensCardLayout,
  LensCardType,
  LensDataset,
  LensInsight,
  LensLabelFormat,
  LensResolvedPeriod,
  LensValueFormat,
} from '~/types/leLens';

import AskRichardWorking from '../AskRichardWorking';
import { LensCalendarContext } from '../LensCalendarContext';
import { cardFilterReach, selectionForCard } from '../cardFilterReach';
import { DEFAULT_SIZE, cardHeight, resolveLensCardPresentation } from '../cardLayout';
import {
  type LensMarkPick,
  type LensRange,
  type LensSelection,
  applyRanges,
  applySelection,
  applyView,
  missingViewMeasures,
} from '../cardView';
import { crossFilterA11yProps } from '../crossFilterA11y';
import { applyRowFields, prepareFields } from '../datasetFields';
import { declaredDatasetLabel, humaniseFilterLabel } from '../filterLabels';
import { governedDisplayDataset, useGovernedDisplayMetadata } from '../governedDisplayMetadata';
import { useEffectiveGovernedMeasureColumns } from '../governedMeasureMetadata';
import {
  UNIT_FRACTION_PERCENT,
  accountingCurrencyParts,
  chartCrossFilterColumn,
  displayLensFieldName,
  fieldFormat,
  formatLensLabel,
  formatLensValue,
  inferLensFieldFormat,
  isNumeric,
  sampledSeriesCaption,
  sortRows,
} from '../lensChart';
import { downloadCsv, downloadDataUrl } from '../lensExport';
import { lensInsightScopeNotice } from '../lensSavedInsightScope';
import { columnLabeller, valueLabel } from '../lensTime';
import { type ComputedReading, computeReading } from '../readings/computedReading';

import LensCardChart from './LensCardChart';
import { type LensCardNarrowingFilter } from './LensCardFilterIndicator';
import LensCardShell, { type LensCardEditTab, type LensCardLoadingKind, LensCardShellProps } from './LensCardShell';
import LensChartDescription from './LensChartDescription';
import LensDriversCard from './LensDriversCard';
import { type LensChartHandle } from './LensEChart';
import LensSqlDialog from './LensSqlDialog';
import { LENS_FRAME_GAP_PX, LENS_FRAME_ROW_PX, cardResponsiveSx } from './cardGrid';
import { cardSchemaMismatch, cardSchemaMismatchDetail } from './cardSchemaMismatch';
import {
  LENS_HERO_FONT_FAMILY,
  TABLE_VISIBLE_ROW_CAP,
  describeCardFailure,
  formatCardFreshness,
  formatMetricProvenance,
  formatPartialViewNotice,
  formatResolvedWindow,
  formatRowCount,
  formatTruncatedBanner,
  isUnconfiguredCard,
  resolveCardState,
} from './cardState';
import { commentarySettingsAvailable } from './commentaryInteraction';
import {
  type LensInsightViewScope,
  lensInsightAskRequest,
  lensInsightHasPendingReadings,
  newerAutomaticLensInsight,
} from './insightBoardEvidence';
import { resolveMetricAlignment } from './metricAlignment';
import {
  compactMetricComparisonLabel,
  formatMetricMovement,
  metricMovementTone,
  resolveViewedMetric,
} from './metricComparison';
import { metricFigureHeightLimit } from './metricSizing';
import { type MetricTrend, resolveMetricTrend } from './metricTrend';
import { type MetricHeadline, resolveMetricHeadline } from './metricValue';
import { missingParameterGuidance } from './missingParameterGuidance';
import { buildComparisonTable, comparisonTableConfig, namedTableRatioFormats } from './tableComparison';
import { type TableSortState, nextTableSort, sortTableRows } from './tableSort';
import { unboundCardCopy } from './unboundCardCopy';
import { wandTargetAttribute } from './wandGeometry';

const NONE_LABEL = 'None';
/** The metric card's headline figure, in Suisse Works: 36 to 44px per the brand
 *  guide. Picked mid-range so a value reads as a hero number without crowding
 *  the comparison caption underneath it on a compact card. */
const METRIC_FIGURE_FONT_SIZE = 40;
/** One line, tight: a display figure never wraps. Also the figure's own
 *  minHeight multiplier below, so it keeps real, visible height (never the
 *  zero a squeezed flex ancestor could otherwise resolve it to) whatever
 *  the card around it has room for. */
const METRIC_FIGURE_LINE_HEIGHT = 1.2;
const TIGHT_METRIC_FIGURE_FONT_SIZE = 22;

/**
 * The numeric fallback remains concrete for non-container-query environments
 * and deterministic rendering tests. Modern browsers then scale it from the
 * card's own inline width, which is the only width that matters once a rail
 * or editor pane has reduced the visible canvas.
 */
const METRIC_FIGURE_RESPONSIVE_SX = {
  '@container (max-width: 560px)': { fontSize: 'clamp(1rem, 8cqi, 2.25rem)' },
  '@container (min-width: 880px)': { fontSize: 'clamp(2.5rem, 5cqi, 2.75rem)' },
};

const CARD_ICONS: Record<LensCardType, SvgIconComponent> = {
  metric: InsightsIcon,
  line: AutoGraphIcon,
  bar: BarChartIcon,
  drivers: BarChartIcon,
  stacked: StackedBarChartIcon,
  area: ShowChartIcon,
  scatter: BubbleChartIcon,
  pie: DonutLargeIcon,
  combo: MultilineChartIcon,
  heatmap: GridOnIcon,
  boxplot: CandlestickChartIcon,
  table: TableChartIcon,
  insight: LightbulbOutlinedIcon,
};

const CHART_CARD_TYPES: ReadonlyArray<LensCardType> = [
  'line',
  'bar',
  'drivers',
  'stacked',
  'area',
  'scatter',
  'pie',
  'combo',
  'heatmap',
  'boxplot',
];

function isChartCardType(type: LensCardType): boolean {
  return CHART_CARD_TYPES.includes(type);
}

// The mark emphasis treatment (LensChartBody/lensEChartsOption) only paints
// bar and stacked marks, because those are the only chart types a click ever
// came from. The axis exemption below must match that exact scope: on any
// other chart type there is no emphasis styling to show a reader why marks
// they did not click are still on screen, so exempting the axis there would
// just make the chart quietly stop reflecting the row narrowing everything
// else on the card (row count, table, export) agrees on.
function isCrossFilterEmphasisChartType(type: LensCardType): boolean {
  return type === 'bar' || type === 'drivers' || type === 'stacked';
}

/**
 * The card footer leads with the thing a reader can recognise and act on: its
 * source name. A governed source gets a quiet “Data catalogue” detail beside
 * it; bespoke SQL is described in reader language as a custom analysis. Full
 * SQL, table and period remain available in Data & queries.
 */
export function formatCardSourceLabel(dataset: LensDataset | undefined): string {
  if (dataset?.source?.kind === 'governed') {
    return dataset.name;
  }
  return dataset?.name || 'Saved query';
}

export type LensCardViewProps = {
  card: LensCard;
  /** Title of an earlier card with the same source, visual and reduction. */
  duplicateOf?: string;
  groupingChanged?: boolean;
  drafting?: boolean;
  data?: LensCardData;
  /** Optional equivalent-period result for metrics, tables, drivers and daily trends. */
  comparison?: { data?: LensCardData; isFetching: boolean; label: string; period?: LensResolvedPeriod };
  canEdit: boolean;
  /** The owner may open a focused formatting action while the Lens is still in view mode. */
  canConfigure?: boolean;
  /** View mode is deliberately a reading surface; edit-only card chrome and
   * provenance appear only after the owner chooses Edit Lens. */
  editorMode?: boolean;
  showActions?: boolean;
  datasets: Array<LensDataset>;
  /** Whether the dashboard-wide data query is still in flight, so a card
   *  without an entry yet can tell "loading" from "backend has nothing". */
  isFetchingData: boolean;
  dateRange: { from?: string; to?: string };
  hasActiveFilters: boolean;
  /** The viewer changed the board's opening scope; saved prose does not follow those changes. */
  savedInsightScopeChanged?: boolean;
  insightScope?: LensInsight['scope'];
  onClearFilters: () => void;
  onCollapseRows?: () => void;
  /** Present only when this card is rendered inside the unified askRichard shell. */
  lensId?: string;
  boardEvidence?: string;
  onAskRichard?: () => void;
  /** Sends a repair request with this card and its mismatched fields already in context. */
  onRepair?: (mismatch: { missing: Array<string>; returned: Array<string> }) => void;
  /** A catalogue-backed source name in the footer becomes a link to that dataset's Data catalogue
   *  detail screen when this is set. A custom-analysis footer stays plain text regardless. */
  onOpenCatalogueDataset?: (catalogueId: string) => void;
  /** Makes this card the explicit subject of the Lens composer without opening chat. */
  onCardSelect?: () => void;
  /** The composer currently carries this card as its selected subject. */
  isSelected?: boolean;
  /**
   * Set while a field row in the panel's field list (FieldList.tsx) is
   * hovered and this card's view uses it. Draws the same outline as
   * isSelected/isBulkSelected, plus a small "uses <field>" caption.
   */
  highlightedField?: string;
  /** Explicit canvas multi-select is kept separate from the Lens composer subject. */
  selectionMode?: boolean;
  isBulkSelected?: boolean;
  onToggleBulkSelection?: () => void;
  onDelete: () => Promise<void>;
  /** Omitted when there is nothing to clone into, which hides the menu item. */
  onDuplicate?: () => Promise<void>;
  onMoveToSection?: NonNullable<LensCardShellProps['onMoveToSection']>;
  /** Saves an order set by dragging a category past its neighbour. */
  onReorderCategories?: (order: Array<string>) => Promise<void>;
  onEdit: (tab?: LensCardEditTab) => void;
  /** Opens the focused Commentary controls rather than the whole Format area. */
  onEditCommentary?: () => void;
  /** Opens the focused Sort controls from either view or edit mode. */
  onEditSort?: () => void;
  onRefresh: () => void;
  /** Writes a completed insight into the Lens spec. Omitted for viewers. */
  onPersistInsight?: (insight: LensInsight) => Promise<void>;
  onSelect: (pick: LensMarkPick) => void;
  selection?: LensSelection;
  boardFilters?: Array<LensCardNarrowingFilter & { column?: string; filterId?: string }>;
  isCrossFilterSource?: boolean;
  ranges?: Record<string, LensRange>;
  isRefreshing: boolean;
  insightRefresh: number;
  /** Columns this card occupies out of the desktop 8-column grid, after its
   *  row was balanced by distributeSpans. */
  span: number;
  /** Saved 12-column frame used by the direct-manipulation canvas. */
  canvasLayout?: LensCardLayout;
  /** dnd-kit binds its node ref and translation to the outer grid frame. */
  canvasNodeRef?: (element: HTMLElement | null) => void;
  canvasStyle?: React.CSSProperties;
  dragHandle?: NonNullable<LensCardShellProps['dragHandle']>;
};

/**
 * Every Lens card, of every type, renders through here and down into
 * LensCardShell. This is the only place a card's fetch result is turned into
 * a body: an insight card never touches `data` (it is not query-backed), so
 * it is a fully separate branch with its own state, kept as a distinct
 * mounted component so its streaming effect only ever runs for insight cards.
 */
export default function LensCardView(props: LensCardViewProps) {
  if (props.card.type === 'insight') {
    return <LensInsightCardView {...props} />;
  }
  return <LensDataCardView {...props} />;
}

function LensDataCardView({
  card,
  duplicateOf,
  groupingChanged = false,
  drafting = false,
  data,
  comparison,
  canEdit,
  canConfigure = false,
  editorMode = true,
  showActions,
  datasets,
  isFetchingData,
  dateRange,
  hasActiveFilters,
  onClearFilters,
  onCollapseRows,
  onAskRichard,
  onRepair,
  onCardSelect,
  isSelected,
  highlightedField,
  selectionMode,
  isBulkSelected,
  onToggleBulkSelection,
  onDelete,
  onDuplicate,
  onMoveToSection,
  onOpenCatalogueDataset,
  onReorderCategories,
  onEdit,
  onEditCommentary,
  onEditSort,
  onRefresh,
  onSelect,
  selection: boardSelection,
  boardFilters = [],
  isCrossFilterSource,
  ranges,
  isRefreshing,
  span,
  canvasLayout,
  canvasNodeRef,
  canvasStyle,
  dragHandle,
}: LensCardViewProps) {
  const theme = useTheme();
  const displayMetadata = useGovernedDisplayMetadata();
  const chartHandleRef = useRef<LensChartHandle>(null);
  const [isSqlOpen, setIsSqlOpen] = useState(false);
  const [isDataOpen, setIsDataOpen] = useState(false);
  const [showRawValues, setShowRawValues] = useState(false);
  const canOpenCommentarySettings = commentarySettingsAvailable(editorMode, canConfigure);
  // A metric always uses the KPI card grammar. In particular, an older card
  // or one created from askRichard may not have a persisted `size` yet.
  const { compact: presentationCompact } = resolveLensCardPresentation(card);
  const Icon = CARD_ICONS[card.type];
  // A metric is one composition: its title, subtitle and headline figure
  // travel together. Cards with no saved alignment use the KPI-centred
  // default; an invalid historic value gets that same safe fallback.
  const metricAlignment = resolveMetricAlignment(card.config?.titleAlignment);
  const selection = useMemo(
    () => selectionForCard(boardSelection, card.view?.where),
    [boardSelection, card.view?.where],
  );

  // Narrow first, then cut: a cross-filter selection narrows the dataset's
  // rows, and the card's view reduces what is left to the shape it draws.
  // Memoised on the card's own inputs, so a click that changes selection or
  // ranges elsewhere on the dashboard does not re-run this card's reshape
  // unless one of its own inputs actually changed.
  // A card reads a shared dataset or its own query, never both.
  const boundDataset = card.datasetId ? datasets.find((entry) => entry.id === card.datasetId) : undefined;
  const effectiveColumns = useEffectiveGovernedMeasureColumns(boundDataset);
  // A ratio view evaluates SUM(of) / SUM(per), so its output is always a
  // unit fraction. The governed measure that declared that exact ratio owns
  // the presentation format; its output alias need not resemble the measure
  // name (and must not be guessed from it).
  const ratioMeasure = card.view?.ratio
    ? effectiveColumns?.find(
        (column) => column.ratio?.of === card.view?.ratio?.of && column.ratio?.per === card.view?.ratio?.per,
      )
    : undefined;
  const ratioFormat = card.view?.ratio ? (ratioMeasure?.format ?? UNIT_FRACTION_PERCENT) : undefined;
  const ratioOutput = card.view?.value ?? card.view?.values?.[0] ?? card.view?.ratio?.of;
  const tableRatioFormat = card.view?.ratio
    ? (card.config?.format ?? (ratioOutput ? card.config?.fieldFormats?.[ratioOutput] : undefined) ?? ratioFormat)
    : undefined;

  // Calculated fields, checked once against what the query actually returned.
  // Row-level ones are folded in before anything else runs, because by the time
  // a reader filters, groups or exports, they are columns like any other; the
  // aggregate ones are handed to applyView, which evaluates them on each
  // group's totals.
  const prepared = useMemo(
    () => prepareFields(boundDataset?.fields, data?.columns ?? []),
    [boundDataset?.fields, data?.columns],
  );
  const enriched = useMemo(() => {
    if (!data?.rows || !data?.columns) {
      return { rows: data?.rows, columns: data?.columns };
    }
    if (!prepared.row.length) {
      return { rows: data.rows, columns: data.columns };
    }
    return {
      rows: applyRowFields(data.rows, prepared.row),
      columns: [...data.columns, ...prepared.row.map((entry) => entry.field.name)],
    };
  }, [data?.rows, data?.columns, prepared.row]);

  const shaped = useMemo(() => {
    if (!enriched.rows || !enriched.columns) {
      return { rows: enriched.rows, columns: enriched.columns };
    }
    return applyView(
      applyRanges(applySelection(enriched.rows, enriched.columns, selection), enriched.columns, ranges),
      enriched.columns,
      card.view,
      card.type,
      prepared.aggregate,
      data?.aggregated,
      effectiveColumns,
    );
  }, [enriched, card.view, card.type, selection, ranges, prepared.aggregate, data?.aggregated, effectiveColumns]);

  // A card keeps its view when the query behind it is rewritten, so it can
  // name a measure the result no longer returns. Shaping reduces that to one
  // null, and a null headline reads as "the query returned nothing", which
  // sends a reader to the SQL when the fault is the card's field choice.
  const missingMeasures = useMemo(
    () =>
      enriched.columns
        ? missingViewMeasures(card.view, enriched.columns, prepared.aggregate, effectiveColumns, card.type)
        : [],
    [card.view, card.type, enriched.columns, prepared.aggregate, effectiveColumns],
  );

  // A bar or stacked chart's own axis must not be the thing a click on it
  // erases: narrowing every mark down to the one just clicked would leave the
  // chart with nothing left to highlight against, which defeats the point of
  // "what did I click". Ranges and every other column still narrow normally;
  // only the exact column this card's own marks are grouped by is exempted.
  // Worked out off the view applied to every row except the cross-filter
  // selection itself, so the exemption never shrinks as a side effect of the
  // very selection it exists to survive.
  const naturalShaped = useMemo(() => {
    if (!enriched.rows || !enriched.columns) {
      return { rows: enriched.rows, columns: enriched.columns };
    }
    return applyView(
      applyRanges(enriched.rows, enriched.columns, ranges),
      enriched.columns,
      card.view,
      card.type,
      prepared.aggregate,
      data?.aggregated,
      effectiveColumns,
    );
  }, [enriched, card.view, card.type, ranges, prepared.aggregate, data?.aggregated, effectiveColumns]);
  // The card's own rows before ranges or any cross-filter selection narrow
  // them, restricted to exactly the columns this card's view declares (its x,
  // series split and measures), the same way `shaped` is. A chart's colour
  // universe (LensChartBody) is built from this: passing the raw, un-viewed
  // `enriched` columns instead let a shared dataset's every declared measure
  // read as a separate series, which put a single-measure card anywhere but
  // slot 0 the moment the dataset behind it had more than one measure.
  const unfilteredViewShaped = useMemo(() => {
    if (!enriched.rows || !enriched.columns) {
      return { rows: enriched.rows, columns: enriched.columns };
    }
    return applyView(
      enriched.rows,
      enriched.columns,
      card.view,
      card.type,
      prepared.aggregate,
      data?.aggregated,
      effectiveColumns,
    );
  }, [enriched, card.view, card.type, prepared.aggregate, data?.aggregated, effectiveColumns]);
  const chartCrossFilterCol = useMemo(
    () =>
      isCrossFilterSource && (card.type === 'line' || card.type === 'area')
        ? card.view?.series
        : isCrossFilterEmphasisChartType(card.type) && naturalShaped.rows && naturalShaped.columns
          ? chartCrossFilterColumn(naturalShaped.rows, naturalShaped.columns, card.config, card.view)
          : undefined,
    [card.type, card.config, card.view, naturalShaped, isCrossFilterSource],
  );
  const chartSelection = useMemo(() => {
    if (!chartCrossFilterCol || !selection || !(chartCrossFilterCol in selection)) {
      return selection;
    }
    const next = { ...selection };
    delete next[chartCrossFilterCol];
    return next;
  }, [selection, chartCrossFilterCol]);
  const chartShaped = useMemo(() => {
    if (chartSelection === selection || !enriched.rows || !enriched.columns) {
      return shaped;
    }
    return applyView(
      applyRanges(applySelection(enriched.rows, enriched.columns, chartSelection), enriched.columns, ranges),
      enriched.columns,
      card.view,
      card.type,
      prepared.aggregate,
      data?.aggregated,
      effectiveColumns,
    );
  }, [
    chartSelection,
    selection,
    shaped,
    enriched,
    card.view,
    card.type,
    ranges,
    prepared.aggregate,
    data?.aggregated,
    effectiveColumns,
  ]);
  // A capped or failed companion cannot support a movement, driver or reading.
  const comparisonData =
    comparison?.data?.status === 'ready' && !comparison.data.truncated ? comparison.data : undefined;
  const comparisonShaped = useMemo(() => {
    if (!comparisonData?.rows || !comparisonData.columns) {
      return { rows: comparisonData?.rows, columns: comparisonData?.columns };
    }
    const comparisonPrepared = prepareFields(boundDataset?.fields, comparisonData.columns);
    const comparisonRows = comparisonPrepared.row.length
      ? applyRowFields(comparisonData.rows, comparisonPrepared.row)
      : comparisonData.rows;
    const selectedRows = applySelection(comparisonRows, comparisonData.columns, chartSelection);
    return applyView(
      card.type === 'table' ? applyRanges(selectedRows, comparisonData.columns, ranges) : selectedRows,
      comparisonData.columns,
      card.view,
      card.type,
      comparisonPrepared.aggregate,
      comparisonData.aggregated,
      effectiveColumns,
    );
  }, [boundDataset?.fields, effectiveColumns, card.type, card.view, chartSelection, comparisonData, ranges]);

  const tableRatioFormats = useMemo(() => namedTableRatioFormats(card, effectiveColumns), [card, effectiveColumns]);
  const tablePresentation = useMemo(() => {
    if (card.type !== 'table' || !shaped.rows || !shaped.columns || (!comparison && !card.config?.tableColumns))
      return undefined;
    const presentation = buildComparisonTable({
      card,
      current: { rows: shaped.rows, columns: shaped.columns },
      comparison:
        !comparison?.isFetching && comparisonShaped.rows && comparisonShaped.columns
          ? { rows: comparisonShaped.rows, columns: comparisonShaped.columns }
          : undefined,
      hasComparison: Boolean(comparison),
      currentComplete: Boolean(data && ['ready', 'empty'].includes(data.status) && !data.truncated),
      ratioFormat: tableRatioFormat,
      ratioFormats: tableRatioFormats,
    });
    return comparison?.isFetching ? { ...presentation, notice: 'Loading comparison…' } : presentation;
  }, [card, shaped, comparison, comparisonShaped, data, tableRatioFormat, tableRatioFormats]);
  const displayedShaped = tablePresentation?.shaped ?? shaped;

  const reach = useMemo(
    () => cardFilterReach(boardSelection, card.view?.where, data),
    [boardSelection, card.view?.where, data],
  );
  const fieldLabel = (column: string) => humaniseFilterLabel(column, declaredDatasetLabel(column, datasets));
  const boardFiltersOn = (column: string) =>
    boardFilters.filter(
      (filter) =>
        (filter.filterId ? (card.filterBindings?.[filter.filterId]?.column ?? filter.column) : filter.column) ===
        column,
    );
  const boardFilterLabel = (column: string) =>
    boardFiltersOn(column)
      .filter((filter) => !filter.filterId)
      .map((filter) => filter.label)
      .join(', ') || fieldLabel(column);
  const pinnedColumns = reach.pinned.map((entry) => entry.column);
  const narrowedBy = [
    ...new Set([
      ...reach.narrowedBy.flatMap((column) => {
        const matching = boardFiltersOn(column).filter((filter) =>
          filter.filterId
            ? !data?.appliedFilters || data.appliedFilters.includes(filter.filterId)
            : !pinnedColumns.includes(column),
        );
        return matching.length ? matching : [{ label: fieldLabel(column) }];
      }),
      ...boardFilters.filter((filter) => filter.filterId && data?.appliedFilters?.includes(filter.filterId)),
    ]),
  ];
  const crossFilterUnaffectedNote =
    [
      ...(data?.unappliedFilters?.length
        ? [`Cannot apply: ${data.unappliedFilters.join(', ')}. This card needs a compatible dataset field.`]
        : []),
      ...reach.pinned.map(
        (entry) =>
          `${boardFilterLabel(entry.column)} does not apply: this card shows ${
            entry.in?.length
              ? `only ${fieldLabel(entry.column)} ${entry.in.join(', ')}`
              : `no ${fieldLabel(entry.column)} ${entry.notIn?.join(', ')}`
          }.`,
      ),
      ...reach.unreachable.map(
        (column) => `${boardFilterLabel(column)} does not apply: this card's data has no ${fieldLabel(column)} field.`,
      ),
    ].join(' ') || undefined;

  const missingGroupingFields = [card.view?.x, card.view?.series].filter(
    (field) =>
      groupingChanged &&
      field &&
      enriched.columns &&
      !enriched.columns.some((column) => column.toLowerCase() === field.toLowerCase()),
  );
  const shapedRowCount = missingGroupingFields.length ? 0 : (displayedShaped.rows?.length ?? 0);
  const stateData =
    tablePresentation && data?.status === 'empty' && shapedRowCount > 0 ? { ...data, status: 'ready' as const } : data;
  const resolvedState = resolveCardState({ data: stateData, isFetching: isFetchingData, shapedRowCount });
  const schemaMismatch = useMemo(
    () =>
      enriched.columns && data?.status === 'ready'
        ? cardSchemaMismatch(card.view, enriched.columns, prepared.aggregate, effectiveColumns, card.type)
        : undefined,
    [card.view, card.type, data?.status, enriched.columns, prepared.aggregate, effectiveColumns],
  );
  const state: LensCardState = schemaMismatch
    ? 'error'
    : drafting && card.brief && !card.datasetId
      ? 'loading'
      : resolvedState;
  // A capped result carries a headline, explanation and warning banner. It
  // cannot safely inhabit a compact tile, even if that was the saved frame:
  // preserve the warning and reserve its full grid frame rather than letting
  // the card overflow into the next one.
  const height = cardHeight(card.size, state === 'truncated' && card.height !== 'xtall' ? 'tall' : card.height);
  // The saved canvas frame is a deliberate direct-manipulation decision. Use
  // its physical row height for rendering rather than letting a card's former
  // Smart-arrange size expand it again after the editor has resized it.
  // Set from the shell once laid out. Held here because the plot height is
  // computed here, and a card that estimated its own chrome wrongly is a plot
  // that overflows the card and loses whatever sits lowest in it.
  const [measuredBodyHeight, setMeasuredBodyHeight] = useState<number | undefined>(undefined);
  const onBodyMeasured = useCallback(
    (next: number) => setMeasuredBodyHeight((current) => (current === next || next <= 0 ? current : next)),
    [],
  );

  const canvasPixelHeight = canvasLayout
    ? canvasLayout.h * LENS_FRAME_ROW_PX + Math.max(0, canvasLayout.h - 1) * LENS_FRAME_GAP_PX
    : undefined;
  // Resizing a formerly large card down to a compact KPI must change the
  // card's composition as well as its outer frame. In particular, the
  // generated request subtitle is helpful at normal height but should never
  // consume the room required for the title, headline and provenance in a
  // short canvas frame.
  const isCompact =
    presentationCompact ||
    (canvasPixelHeight !== undefined && canvasPixelHeight < cardHeight('medium', 'standard').card);
  const isTightMetric = card.type === 'metric' && canvasPixelHeight !== undefined && canvasPixelHeight < 160;
  const renderedCardMinHeight =
    canvasPixelHeight === undefined ? height.card : Math.min(height.card, canvasPixelHeight);
  // A canvas resize is an explicit decision about the whole visual frame.
  // Do not retain the card type's natural-body ceiling here: doing so left a
  // tall chart with a 400px plot floating in the middle of a much taller
  // frame. The shell reserves its title and padding, then the chart fills
  // everything else the frame makes available.
  // What the shell puts around the plot: the title block and content padding
  // always, plus a footer only for a state that must stay visible.
  // Normal provenance now lives in the three-dot menu. Keep a footer only for
  // exceptional states that must remain visible while reading the board.
  const showsFooterRow = state === 'truncated' || Boolean(card.contract) || Boolean(duplicateOf);
  const reservedChrome = (isTightMetric ? 44 : 64) + (showsFooterRow ? 32 : 0);
  // The estimate above is what the plot gets until the browser has laid the
  // card out; after that the shell reports what the plot really has, which is
  // the only number that knows how many lines the description wrapped to. Kept
  // as an estimate first rather than rendering nothing, so the card draws on
  // its first paint.
  const estimatedBodyHeight =
    canvasPixelHeight === undefined ? height.body : Math.max(64, canvasPixelHeight - reservedChrome);
  const renderedBodyHeight = measuredBodyHeight ?? estimatedBodyHeight;

  const dataset = boundDataset;
  const displayCard: LensCard = useMemo(() => {
    const sourceFields = [
      ...(governedDisplayDataset(dataset, displayMetadata)?.columns ?? []),
      ...(dataset?.fields ?? []),
    ];
    const measure =
      sourceFields.find((field) => field.name === card.view?.value) ??
      dataset?.columns?.find(
        (field) =>
          field.ratio &&
          card.view?.ratio &&
          field.ratio.of === card.view.ratio.of &&
          field.ratio.per === card.view.ratio.per,
      );
    const fieldLabels = Object.fromEntries(
      sourceFields.filter((field) => field.label).map((field) => [field.name, field.label!]),
    );
    // applyView may put a derived ratio under its numerator's stable key.
    // That output names the ratio, not the numerator's additive measure.
    if (card.view?.ratio && ratioOutput) {
      fieldLabels[ratioOutput] =
        ratioMeasure?.label ??
        `${displayLensFieldName(card.view.ratio.of, { fieldLabels })} / ${displayLensFieldName(card.view.ratio.per, { fieldLabels })}`;
    }
    const config = {
      format: measure?.format,
      ...card.config,
      fieldLabels: {
        ...fieldLabels,
        ...card.config?.fieldLabels,
      },
      fieldFormats: {
        // Ordinary tables have no comparison presentation to supply these formats.
        // Keep governed read metadata and named-ratio units per column, never on the saved card.
        ...(card.type === 'table'
          ? Object.fromEntries(
              (effectiveColumns ?? []).filter((field) => field.format).map((field) => [field.name, field.format!]),
            )
          : {}),
        ...Object.fromEntries(sourceFields.filter((field) => field.format).map((field) => [field.name, field.format!])),
        ...card.config?.fieldFormats,
        ...tableRatioFormats,
      },
    };
    return {
      ...card,
      config: tablePresentation
        ? comparisonTableConfig(
            config,
            tablePresentation.columns,
            comparison?.label ?? 'Comparison',
            tableRatioFormat,
            tableRatioFormats,
          )
        : config,
    };
  }, [
    card,
    dataset,
    displayMetadata,
    effectiveColumns,
    ratioMeasure,
    ratioOutput,
    tablePresentation,
    comparison?.label,
    tableRatioFormat,
    tableRatioFormats,
  ]);
  const cardSql = state === 'restricted' ? undefined : (data?.query?.sql ?? dataset?.sql);
  const source = formatCardSourceLabel(dataset);
  const catalogueId = dataset?.source?.kind === 'governed' ? dataset.source.catalogueId : undefined;
  const sourceDetail = undefined;
  const freshness = formatCardFreshness(data?.refreshedAt);

  let metricResult: MetricHeadline | null = null;
  if (card.type === 'metric') {
    metricResult = missingMeasures.length
      ? {
          kind: 'refused',
          reason: `This card is set to show ${missingMeasures.join(' and ')}, which the query does not return. Edit the card and pick one of the fields it returns.`,
        }
      : resolveViewedMetric(data ?? {}, card.view, boundDataset?.fields, selection, ranges);
  }
  // The sparkline beside the headline: the same rows, the same aggregate, one
  // level finer (per date). Nothing extra is fetched, so a metric card whose
  // dataset is not keyed on a date simply shows no trend.
  const metricTrend = useMemo(() => {
    if (card.type !== 'metric' || metricResult?.kind !== 'value' || !enriched.rows || !enriched.columns) {
      return null;
    }
    return resolveMetricTrend(
      applyRanges(applySelection(enriched.rows, enriched.columns, selection), enriched.columns, ranges),
      enriched.columns,
      card.view,
      prepared.aggregate,
      metricResult.column,
      data?.aggregated,
    );
  }, [card.type, card.view, enriched, metricResult, prepared.aggregate, ranges, selection, data?.aggregated]);

  // The card's own reading, from the rows it is already drawing. No fetch and
  // no model call: this is the same `shaped` the table, row count and export
  // read, so the sentence can never describe a different cut than the card.
  // A narrative reading is a section-level concern and is not resolved here.
  const calendar = useContext(LensCalendarContext);
  const computedReading = useMemo((): ComputedReading | undefined => {
    if (card.reading?.mode === 'narrative') return undefined;
    const format = ratioFormat ?? card.config?.format;
    const against =
      comparison && comparisonShaped.rows && comparisonShaped.columns
        ? { shaped: { rows: comparisonShaped.rows, columns: comparisonShaped.columns }, label: comparison.label }
        : undefined;
    return computeReading({
      type: card.type,
      calendar,
      ...(card.reading?.mode === 'computed' && card.reading.template ? { template: card.reading.template } : {}),
      shaped,
      ...(against ? { comparison: against } : {}),
      ...(format ? { format } : {}),
      ...(card.view?.x ? { dimension: card.view.x } : {}),
      ...(card.view?.value ? { measure: card.view.value } : {}),
      ...(card.view?.series ? { series: card.view.series } : {}),
      ...(data?.truncated ? { truncated: true } : {}),
      ...(card.view?.ratio ? { isRatio: true } : {}),
      ...(card.view?.limit ? { limited: true } : {}),
      // The chart resolves its format per column, so the reading asks the same
      // way rather than printing a bare number under an axis reading dollars.
      formatFor: (column: string) => fieldFormat(card.config, column),
      ...(metricTrend ? { trend: { dates: metricTrend.dates, values: metricTrend.values } } : {}),
      dateRange,
    });
  }, [
    card.reading,
    card.type,
    card.config?.format,
    card.view?.ratio,
    card.view?.limit,
    card.view?.value,
    card.view?.x,
    card.config,
    comparison,
    comparisonShaped,
    data?.truncated,
    dateRange,
    metricTrend,
    ratioFormat,
    shaped,
    calendar,
  ]);
  // Computed chart descriptions are quiet disclosures beneath the plot.
  // Legacy right-side placement remains accepted in saved specs.
  const commentaryVisible = card.config?.commentary?.show !== false;
  const comparisonMetricResult = useMemo(() => {
    if (card.type !== 'metric' || !comparisonData?.rows || !comparisonData.columns) {
      return null;
    }
    return resolveViewedMetric(comparisonData, card.view, boundDataset?.fields, selection, ranges);
  }, [card.type, card.view, comparisonData, boundDataset?.fields, ranges, selection]);

  const parameterGuidance = missingParameterGuidance(data?.missingParameters, card.filterBindings, canEdit);

  let rowCountText: string | undefined;
  if (state === 'restricted') {
    // The row count is exactly what a restricted viewer cannot see: the
    // footer marks the restriction instead of guessing a number from data
    // that never arrived.
  } else if (parameterGuidance) {
    rowCountText = parameterGuidance.footer;
  } else if (state === 'truncated') {
    rowCountText = formatTruncatedBanner(data?.rows?.length ?? shapedRowCount, data?.totalRowCount);
  } else if (card.type === 'table') {
    rowCountText =
      shapedRowCount > TABLE_VISIBLE_ROW_CAP
        ? formatPartialViewNotice(TABLE_VISIBLE_ROW_CAP, shapedRowCount)
        : formatRowCount(shapedRowCount);
  } else if (card.type === 'metric' && metricResult) {
    rowCountText =
      metricResult.kind === 'value'
        ? formatMetricProvenance(metricResult.rowCount, metricResult.aggregate)
        : metricResult.kind === 'refused'
          ? missingMeasures.length
            ? 'Field not in this query'
            : 'Needs a view to combine these rows'
          : formatRowCount(0);
  } else if (isChartCardType(card.type)) {
    // Above the mark ceiling the chart itself draws a sample rather than
    // every row; the footer says so instead of a plain count that would
    // otherwise imply every point made it onto the plot.
    rowCountText = sampledSeriesCaption(shapedRowCount) ?? formatRowCount(shapedRowCount);
  }

  const emptyInfo = dataset?.deletedAt
    ? {
        headline: 'Dataset deleted.',
        subtitle: canEdit
          ? 'Restore it in Data & queries or choose another source.'
          : 'Ask the owner to restore this dataset or choose another source.',
        actionLabel: canEdit ? 'Choose source' : undefined,
        onAction: canEdit ? onEdit : undefined,
      }
    : missingGroupingFields.length > 0
      ? {
          headline: 'Selected fields are not in the loaded data.',
          subtitle: canEdit
            ? 'Save the card to load the new grouping.'
            : 'Refresh the Lens to load the selected fields.',
        }
      : card.brief && !card.datasetId
        ? {
            ...unboundCardCopy(card, canConfigure),
            onAction: canConfigure ? onEdit : undefined,
          }
        : isUnconfiguredCard(data)
          ? {
              headline: parameterGuidance?.headline ?? 'No query connected.',
              subtitle:
                parameterGuidance?.subtitle ??
                (canEdit ? 'Add a query or connect a dataset to this card.' : 'This card has not been set up yet.'),
              actionLabel: canEdit ? 'Edit card' : undefined,
              onAction: canEdit ? onEdit : undefined,
            }
          : {
              headline:
                data?.status === 'empty' &&
                data.message === 'Select a comparison period to calculate this cross-window value.'
                  ? data.message
                  : 'No rows for this period.',
              subtitle: formatResolvedWindow(dateRange),
              actionLabel: hasActiveFilters ? 'Clear filters' : undefined,
              onAction: hasActiveFilters ? onClearFilters : undefined,
            };

  const loadingKind: LensCardLoadingKind =
    card.type === 'metric' ? 'metric' : card.type === 'table' ? 'table' : 'chart';
  const canExportCsv =
    !missingGroupingFields.length && Boolean(displayedShaped.rows?.length && displayedShaped.columns?.length);
  const canExportImage =
    card.type !== 'drivers' && isChartCardType(card.type) && (state === 'ready' || state === 'truncated');

  return (
    <Box
      // Read by the board PDF export (lensPdfExport.ts): the rows this card shows after its
      // filters and view, and its footer's row line when that line says the view is partial.
      data-lens-pdf-shaped-rows={shapedRowCount}
      data-lens-pdf-row-note={rowCountText}
      data-lens-pdf-row-note-partial={
        state === 'truncated' || (card.type === 'table' && shapedRowCount > TABLE_VISIBLE_ROW_CAP) ? 'true' : undefined
      }
      onClickCapture={(event) => {
        // React portal events still reach this owner; menu/dialog actions are outside the card body.
        if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target)) return;
        const control =
          event.target instanceof Element
            ? event.target.closest<HTMLElement>(
                'button, a, input, textarea, [data-lens-card-description], [role="button"], .react-resizable-handle',
              )
            : null;
        // The card frame itself is a button when it can be selected for
        // askRichard. `closest('[role=button]')` therefore also finds the
        // frame for every click inside it; only a nested control should opt
        // out of selecting the card.
        if (control && control !== event.currentTarget) {
          return;
        }
        if (selectionMode) {
          onToggleBulkSelection?.();
          return;
        }
        onCardSelect?.();
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (!selectionMode && !onCardSelect) {
          return;
        }
        if ((event.key === 'Enter' || event.key === ' ') && !event.nativeEvent.isComposing) {
          event.preventDefault();
          if (selectionMode) {
            onToggleBulkSelection?.();
          } else {
            onCardSelect?.();
          }
        }
      }}
      ref={canvasNodeRef}
      {...(selectionMode || onCardSelect
        ? {
            'aria-label': selectionMode ? `Select ${card.title}` : `Use ${card.title} in askRichard`,
            role: 'button' as const,
            tabIndex: 0,
          }
        : {})}
      style={canvasStyle}
      sx={
        canvasLayout
          ? {
              ...(isSelected || isBulkSelected || highlightedField
                ? {
                    '& [data-lens-card-primary="true"]': {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                  }
                : {}),
              // The grid engine positions and sizes the frame; the card fills
              // it, which is what makes a resize change the card and not just
              // the space around it.
              height: '100%',
              minHeight: 0,
              minWidth: 0,
              position: 'relative',
            }
          : cardResponsiveSx(span, height.card)
      }
    >
      <Box
        sx={{
          alignItems: 'stretch',
          display: 'flex',
          height: canvasLayout ? '100%' : 'auto',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <Box
          data-lens-card-primary="true"
          sx={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0, '& > .MuiCard-root': { flex: 1, minWidth: 0 } }}
        >
          <LensCardShell
            canEdit={canEdit}
            cardMinHeight={renderedCardMinHeight}
            compact={isCompact}
            tight={isTightMetric}
            // A headline card is one aligned unit: title, figure and comparison.
            titleAlignment={card.type === 'metric' ? metricAlignment : card.config?.titleAlignment}
            titleSize={card.config?.titleSize}
            kpiLabel={card.type === 'metric'}
            fillFrame={Boolean(canvasLayout)}
            contract={card.contract}
            duplicateOf={duplicateOf}
            description={highlightedField ? `Uses ${highlightedField}` : card.description}
            dragHandle={dragHandle}
            empty={emptyInfo}
            // The message is passed through describeCardFailure rather than rendered
            // raw: the backend answers a failed request with the thrown error's
            // message and its stack, and neither belongs on a card face.
            error={
              schemaMismatch
                ? {
                    message: 'This card needs a quick repair.',
                    guidance: 'The result changed and no longer matches the fields used by this chart.',
                    detail: cardSchemaMismatchDetail(schemaMismatch),
                    actionLabel: onRepair ? 'Repair with askRichard' : canConfigure ? 'Edit card' : undefined,
                    onAction: onRepair ? () => onRepair(schemaMismatch) : canConfigure ? () => onEdit(1) : undefined,
                    secondaryActionLabel: onRepair && canConfigure ? 'Edit card' : undefined,
                    onSecondaryAction: onRepair && canConfigure ? () => onEdit(1) : undefined,
                  }
                : { ...describeCardFailure(data?.message), onRetry: onRefresh }
            }
            // The API states why a card is withheld; without this the shell fell
            // back to a generic line and the reason never reached the reader.
            restricted={state === 'restricted' && data?.message ? { headline: data.message } : undefined}
            footer={{
              source,
              detail: sourceDetail,
              catalogueId,
              freshness,
              rowCountText,
              restrictedNote: state === 'restricted' ? 'Restricted' : undefined,
              narrowedBy,
              crossFilterUnaffectedNote,
            }}
            datasetName={dataset?.name}
            onOpenCatalogueDataset={onOpenCatalogueDataset}
            reading={
              computedReading && !isTightMetric ? (
                isChartCardType(card.type) ? (
                  commentaryVisible || computedReading.kind === 'refused' ? (
                    <LensChartDescription cardTitle={card.title} reading={computedReading} />
                  ) : undefined
                ) : commentaryVisible ? (
                  <LensCardReading
                    cardTitle={card.title}
                    onOpenSettings={canOpenCommentarySettings ? (onEditCommentary ?? (() => onEdit(3))) : undefined}
                    reading={computedReading}
                  />
                ) : undefined
              ) : undefined
            }
            readingDivider={!isChartCardType(card.type)}
            // Readers can inspect the source and download results through the card menu.
            showActions={
              showActions ??
              (editorMode || canExportCsv || canExportImage || Boolean(cardSql) || Boolean(onCollapseRows))
            }
            actionScope={editorMode ? 'authoring' : 'export-only'}
            showFooter={showsFooterRow}
            icon={<Icon color="primary" />}
            loadingKind={loadingKind}
            onDelete={() => void onDelete()}
            onDuplicate={onDuplicate ? () => void onDuplicate() : undefined}
            onMoveToSection={onMoveToSection}
            onEdit={onEdit}
            onSort={
              canConfigure && ['bar', 'stacked', 'line', 'area', 'combo', 'table'].includes(card.type)
                ? (onEditSort ?? (() => onEdit(3)))
                : undefined
            }
            onAskRichard={onAskRichard}
            onRefresh={onRefresh}
            onCollapseRows={onCollapseRows}
            onViewData={displayedShaped.rows?.length && state !== 'restricted' ? () => setIsDataOpen(true) : undefined}
            onViewSql={cardSql ? () => setIsSqlOpen(true) : undefined}
            onExportCsv={
              canExportCsv ? () => downloadCsv(displayedShaped.rows, displayedShaped.columns, card.title) : undefined
            }
            onExportImage={
              canExportImage
                ? () => {
                    const png = chartHandleRef.current?.toPngDataUrl(theme.palette.background.paper);
                    if (png) {
                      downloadDataUrl(png, card.title);
                    }
                  }
                : undefined
            }
            refreshing={isRefreshing}
            selectionControl={
              selectionMode && onToggleBulkSelection
                ? { checked: Boolean(isBulkSelected), label: `Select ${card.title}`, onChange: onToggleBulkSelection }
                : undefined
            }
            // Descriptions are useful in the Details panel, but duplicate the
            // question and crowd a card's answer in the editor. Keep the card face
            // to title, answer and compact source; nothing is silently ellipsised.
            showDescription={false}
            state={state}
            title={card.title}
            bodyHeight={renderedBodyHeight}
            bodyAlign={
              card.type === 'metric'
                ? isCompact && (state === 'ready' || state === 'loading')
                  ? 'center'
                  : 'top'
                : card.type === 'table' && narrowedBy.length > 0
                  ? 'top'
                  : 'center'
            }
            bodyContentAlignment={card.type === 'metric' ? metricAlignment : 'center'}
            onBodyMeasured={onBodyMeasured}
          >
            {(state === 'ready' || state === 'truncated') && (
              <LensReadyBody
                card={displayCard}
                isTightMetric={isTightMetric}
                metricResult={metricResult}
                metricTrend={metricTrend}
                ratioFormat={ratioFormat}
                periodNote={data?.period?.label.note}
                periodPartial={data?.period?.partial}
                periodPinned={card.period !== undefined}
                textColumns={dataset?.columns
                  ?.filter((column) => column.type?.toUpperCase() === 'STRING')
                  .map((column) => column.name)}
                comparison={
                  comparison
                    ? {
                        isFetching: comparison.isFetching,
                        label: comparison.label,
                        period: comparison.period,
                        result: comparisonMetricResult,
                        shaped: comparisonShaped,
                      }
                    : undefined
                }
                isCompact={isCompact}
                metricAlignment={metricAlignment}
                onSelect={onSelect}
                plotHeight={renderedBodyHeight}
                selection={selection}
                chartRef={chartHandleRef}
                onReorderCategories={onReorderCategories ? (order) => void onReorderCategories(order) : undefined}
                shaped={displayedShaped}
                tableNotice={tablePresentation?.notice}
                chartShaped={chartShaped}
                unfilteredShaped={unfilteredViewShaped}
                theme={theme}
                truncated={state === 'truncated'}
              />
            )}
          </LensCardShell>
        </Box>
      </Box>
      <Dialog open={isDataOpen} onClose={() => setIsDataOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{card.title} — data</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {displayedShaped.rows?.length ?? 0} loaded rows after the current filters.{' '}
            {data?.truncated ? 'The query limit was reached; these rows are a partial result.' : ''}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={showRawValues} onChange={(event) => setShowRawValues(event.target.checked)} />}
            label="Show raw values"
          />
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {displayedShaped.columns?.map((column) => (
                    <TableCell key={column}>{displayLensFieldName(column, displayCard.config)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedShaped.rows?.slice(0, TABLE_VISIBLE_ROW_CAP).map((row, index) => (
                  <TableRow key={index}>
                    {displayedShaped.columns?.map((column) => (
                      <TableCell key={column}>
                        {showRawValues
                          ? row[column] == null
                            ? 'null'
                            : typeof row[column] === 'object'
                              ? JSON.stringify(row[column])
                              : String(row[column])
                          : formatLensValue(
                              row[column],
                              card.view?.ratio && column === ratioOutput
                                ? (card.config?.fieldFormats?.[column] ?? card.config?.format ?? ratioFormat)
                                : fieldFormat(displayCard.config, column),
                            )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(displayedShaped.rows?.length ?? 0) > TABLE_VISIBLE_ROW_CAP && (
            <Typography variant="caption">
              Showing the first {TABLE_VISIBLE_ROW_CAP} rows. Export CSV for all loaded rows.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => downloadCsv(displayedShaped.rows ?? [], displayedShaped.columns ?? [], card.title)}>
            Export CSV
          </Button>
          <Button onClick={() => setIsDataOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {cardSql && (
        <LensSqlDialog
          datasetName={dataset?.name}
          onClose={() => setIsSqlOpen(false)}
          open={isSqlOpen}
          sql={cardSql}
          executed={Boolean(data?.query)}
          parameters={data?.query?.parameters}
          title={card.title}
        />
      )}
    </Box>
  );
}

function LensReadyBody({
  card,
  shaped,
  tableNotice,
  chartShaped,
  unfilteredShaped,
  isCompact,
  isTightMetric,
  metricAlignment,
  onSelect,
  plotHeight,
  selection,
  theme,
  truncated,
  metricResult,
  metricTrend = null,
  ratioFormat,
  comparison,
  periodNote,
  periodPartial,
  periodPinned = false,
  textColumns,
  chartRef,
  onReorderCategories,
}: {
  card: LensCard;
  shaped: { rows?: Array<Record<string, unknown>>; columns?: Array<string> };
  tableNotice?: string;
  /** Same shape as `shaped`, but with this card's own chart axis exempted
   *  from cross-filter narrowing, so a chart card can keep every mark on
   *  screen for the marks-level highlight to work against. Falls back to
   *  `shaped` for every non-chart card type. */
  chartShaped?: { rows?: Array<Record<string, unknown>>; columns?: Array<string> };
  /** The card's rows before ranges and any cross-filter selection narrows
   *  them, forwarded to the chart for a stable series-to-colour assignment
   *  (colour follows the entity, never its rank). See LensChartBody. */
  unfilteredShaped?: { rows?: Array<Record<string, unknown>>; columns?: Array<string> };
  /** A small or medium card ('short' height by default): the metric figure
   *  drops to 36px here rather than the title shrinking below the brand
   *  guide's 15px floor to make room for a two-line heading. */
  isCompact: boolean;
  isTightMetric: boolean;
  metricAlignment: LensCardAlignment;
  onSelect: (pick: LensMarkPick) => void;
  plotHeight: number;
  selection?: LensSelection;
  theme: Theme;
  truncated: boolean;
  metricResult: ReturnType<typeof resolveMetricHeadline> | null;
  /** Per-date points behind the headline, when its dataset is keyed on a date. */
  metricTrend?: MetricTrend | null;
  /** Presentation contract from the governed measure that declared this exact ratio. */
  ratioFormat?: LensValueFormat;
  chartRef?: React.Ref<LensChartHandle>;
  onReorderCategories?: (order: Array<string>) => void;
  comparison?: {
    isFetching: boolean;
    label: string;
    period?: LensResolvedPeriod;
    result: ReturnType<typeof resolveMetricHeadline> | null;
    shaped?: { rows?: Array<Record<string, unknown>>; columns?: Array<string> };
  };
  periodNote?: string;
  periodPartial?: boolean;
  periodPinned?: boolean;
  textColumns?: Array<string>;
}) {
  const metricColumn = metricResult?.kind === 'value' ? metricResult.column : card.view?.value;
  // A governed ratio gets its scale from the measure that declared that exact
  // numerator/denominator pair. Alias names are not a format contract: a
  // value of 1.09 from a ratio is 109%, while a raw `*_pct` SQL column can
  // mean 1.09%.
  const format =
    card.config?.format ??
    (metricColumn ? card.config?.fieldFormats?.[metricColumn] : undefined) ??
    ratioFormat ??
    (!card.view?.ratio && metricColumn
      ? inferLensFieldFormat(metricColumn, metricResult?.kind === 'value' ? [metricResult.value] : [])
      : undefined);
  const labelFormat = card.config?.labelFormat;
  // Reserve room for a wrapped comparison after the shell measures the title
  // and footer. Width-only sizing cannot protect a short card from clipping.
  const figureHeightLimit = metricFigureHeightLimit(plotHeight, Boolean(comparison));
  const metricFigureFontSize = isTightMetric
    ? Math.min(TIGHT_METRIC_FIGURE_FONT_SIZE, figureHeightLimit)
    : Math.min(isCompact ? 40 : METRIC_FIGURE_FONT_SIZE, figureHeightLimit);
  const metricFigureResponsiveSx = {
    ...METRIC_FIGURE_RESPONSIVE_SX,
    '@container (max-width: 560px)': { fontSize: `min(clamp(1rem, 10cqi, 2.5rem), ${figureHeightLimit}px)` },
    '@container (min-width: 880px)': { fontSize: `min(clamp(2.5rem, 5cqi, 2.75rem), ${figureHeightLimit}px)` },
  };
  if (card.type === 'metric') {
    if (truncated) {
      return (
        <Stack
          alignItems={metricAlignment === 'left' ? 'flex-start' : metricAlignment === 'right' ? 'flex-end' : 'center'}
          spacing={0.5}
          sx={{ width: '100%' }}
        >
          <Typography
            component="p"
            sx={{
              fontFamily: LENS_HERO_FONT_FAMILY,
              fontSize: metricFigureFontSize,
              ...metricFigureResponsiveSx,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: METRIC_FIGURE_LINE_HEIGHT,
              minHeight: `${METRIC_FIGURE_LINE_HEIGHT}em`,
              opacity: 0.6,
              textAlign: metricAlignment,
              textDecoration: 'line-through',
            }}
            data-lens-pdf-metric-figure=""
          >
            Partial
          </Typography>
          <Typography color="text.secondary" data-lens-pdf-metric-message="" variant="caption">
            This total is incomplete. See below.
          </Typography>
        </Stack>
      );
    }
    if (!metricResult || metricResult.kind === 'empty') {
      return (
        <Typography color="text.secondary" data-lens-pdf-metric-message="" variant="body2">
          This query returned no number to show.
        </Typography>
      );
    }
    if (metricResult.kind === 'refused') {
      return (
        <Typography
          color="text.secondary"
          data-lens-pdf-metric-message=""
          sx={{ textAlign: metricAlignment }}
          variant="body2"
        >
          {metricResult.reason}
        </Typography>
      );
    }
    // The sparkline is a config choice now, not a side effect of alignment: a
    // centred headline still gets one, drawn under the figure instead of
    // beside it. Short bodies prioritise the value and comparison.
    const sparklineWanted = card.config?.sparkline !== false && plotHeight >= 120;
    const sparkline = metricTrend && sparklineWanted ? <MetricSparkline theme={theme} trend={metricTrend} /> : null;
    const sideBySide = sparkline && metricAlignment !== 'center';
    const headline = (
      <Stack
        alignItems={metricAlignment === 'left' ? 'flex-start' : metricAlignment === 'right' ? 'flex-end' : 'center'}
        spacing={0.5}
        sx={sideBySide ? { flex: 1, minWidth: 0 } : { width: '100%', flexShrink: 0 }}
      >
        <Typography
          component="p"
          data-lens-wand={wandTargetAttribute({
            kind: 'metric',
            label: card.title,
            value: formatLensValue(metricResult.value, format),
          })}
          sx={{
            fontFamily: LENS_HERO_FONT_FAMILY,
            fontSize: metricFigureFontSize,
            ...(isTightMetric ? {} : metricFigureResponsiveSx),
            fontVariantNumeric: 'tabular-nums',
            lineHeight: METRIC_FIGURE_LINE_HEIGHT,
            minHeight: `${METRIC_FIGURE_LINE_HEIGHT}em`,
            textAlign: metricAlignment,
          }}
          title={formatLensValue(metricResult.value, format)}
        >
          {formatMetricFigure(metricResult.value, format)}
        </Typography>
        <MetricComparison
          current={metricResult.value}
          format={format}
          metricAlignment={metricAlignment}
          comparison={comparison}
          note={periodNote}
          partial={periodPartial}
          pinned={periodPinned}
          tight={isTightMetric}
        />
      </Stack>
    );
    if (!sparkline) {
      return headline;
    }
    if (metricAlignment === 'center') {
      return (
        <Stack alignItems="center" spacing={1} sx={{ width: '100%' }}>
          {headline}
          {sparkline}
        </Stack>
      );
    }
    return (
      <Stack
        alignItems="flex-start"
        direction={metricAlignment === 'right' ? 'row-reverse' : 'row'}
        justifyContent="space-between"
        spacing={2}
        sx={{ width: '100%' }}
      >
        {headline}
        {sparkline}
      </Stack>
    );
  }

  if (card.type === 'drivers') {
    const current = chartShaped?.rows && chartShaped.columns ? chartShaped : shaped;
    return (
      <LensDriversCard
        card={card}
        comparison={comparison?.shaped}
        comparisonLabel={comparison?.label}
        current={current}
        format={ratioFormat ?? card.config?.format}
        loadingComparison={Boolean(comparison?.isFetching)}
        onSelect={onSelect}
        plotHeight={plotHeight}
        theme={theme}
      />
    );
  }

  // Which columns this card can raise a selection on. Any card with rows can
  // drive a filter: what makes it coherent is other cards carrying the same
  // column, not this one sharing a query.
  const selectable = Boolean(shaped.rows?.length);
  const labelColumn = card.view?.x;

  if (isChartCardType(card.type) && shaped.rows && shaped.columns) {
    // chartShaped keeps this card's own grouping axis un-narrowed so a
    // cross-filter click can highlight the mark it set rather than erase
    // every other one; every other card-level use of data still reads
    // `shaped`, the fully narrowed row set.
    const forChart = chartShaped?.rows && chartShaped?.columns ? chartShaped : shaped;
    return (
      <LensCardChart
        card={card}
        comparison={
          !comparison?.isFetching && comparison?.shaped?.rows && comparison.shaped.columns
            ? {
                shaped: { rows: comparison.shaped.rows, columns: comparison.shaped.columns },
                period: comparison.period,
              }
            : undefined
        }
        chartRef={chartRef}
        onReorderCategories={onReorderCategories}
        onSelect={onSelect}
        plotHeight={plotHeight}
        selection={selection}
        shaped={{ columns: forChart.columns, rows: forChart.rows }}
        theme={theme}
        truncated={truncated}
        unfilteredShaped={
          unfilteredShaped?.rows && unfilteredShaped?.columns
            ? { rows: unfilteredShaped.rows, columns: unfilteredShaped.columns }
            : undefined
        }
      />
    );
  }

  if (card.type === 'table' && shaped.rows && shaped.columns) {
    const table = (
      <LensTable
        card={card}
        labelColumn={labelColumn}
        labelFormat={labelFormat}
        onSelect={onSelect}
        selectable={selectable}
        selection={selection}
        shaped={{ columns: shaped.columns, rows: shaped.rows }}
        textColumns={textColumns}
      />
    );
    return tableNotice ? (
      <Stack spacing={0.5} sx={{ height: '100%', minHeight: 0, width: '100%' }}>
        <Typography data-lens-table-notice color="text.secondary" variant="caption">
          {tableNotice}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0 }}>{table}</Box>
      </Stack>
    ) : (
      table
    );
  }

  // Every card type is handled above once its state is 'ready'; this is a
  // last-resort message rather than the diagnostic row/column dump it used to
  // fall through to, for a card type not yet covered here.
  return (
    <Typography color="text.secondary" variant="body2">
      This card type is not supported yet.
    </Typography>
  );
}

const TEXT_COLUMN_MIN_WIDTH_PX = 220;

function TableCurrencyValue({ format, value }: { format: LensValueFormat; value: unknown }) {
  const currency = accountingCurrencyParts(value, format);
  if (!currency) {
    return <>{formatValue(value, format)}</>;
  }
  return (
    <Box
      component="span"
      sx={{ alignItems: 'baseline', columnGap: 1, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)' }}
    >
      <Box component="span">{currency.symbol}</Box>
      <Box component="span" sx={{ textAlign: 'right' }}>
        {currency.amount}
      </Box>
    </Box>
  );
}

const isWebLink = (value: unknown): value is string => typeof value === 'string' && /^https?:\/\/\S+$/i.test(value);

function LensTable({
  card,
  labelColumn,
  labelFormat,
  onSelect,
  selectable,
  selection,
  shaped,
  textColumns = [],
}: {
  card: LensCard;
  labelColumn?: string;
  labelFormat?: LensLabelFormat;
  onSelect: (pick: LensMarkPick) => void;
  selectable: boolean;
  selection?: LensSelection;
  shaped: { rows: Array<Record<string, unknown>>; columns: Array<string> };
  textColumns?: Array<string>;
}) {
  const calendar = useContext(LensCalendarContext);
  const cellLabels = useMemo(
    () =>
      Object.fromEntries(
        shaped.columns.map((column) => [
          column,
          columnLabeller(
            shaped.rows.map((row) => row[column]),
            calendar,
            column,
          ),
        ]),
      ),
    [shaped, calendar],
  );
  const mappedValueLabel = (column: string, value: unknown): string | undefined => {
    const labels = card.config?.fieldValueLabels?.[column];
    return value != null && labels && Object.prototype.hasOwnProperty.call(labels, String(value))
      ? labels[String(value)]
      : undefined;
  };
  const [interactiveSort, setInteractiveSort] = useState<TableSortState | null>(null);
  const explicitlyFormatted = (column: string) => Boolean(card.config?.fieldFormats?.[column] ?? card.config?.format);
  const numericColumns = shaped.columns.filter(
    (column) =>
      (!textColumns.includes(column) || explicitlyFormatted(column)) &&
      shaped.rows.some((row) => isNumeric(row[column])),
  );
  const labelColumn2 =
    [labelColumn, card.config?.xColumn].find((column) => column && shaped.columns.includes(column)) ??
    shaped.columns[0];
  const shownAsText = (column: string, value: unknown) =>
    value != null &&
    textColumns.includes(column) &&
    !explicitlyFormatted(column) &&
    !(column === labelColumn2 && labelFormat);
  const configuredSort: TableSortState | null =
    card.config?.sort?.by === 'label'
      ? { column: labelColumn2, direction: card.config.sort.direction ?? 'asc' }
      : card.config?.sort?.by === 'value' && card.config.sort.series && shaped.columns.includes(card.config.sort.series)
        ? { column: card.config.sort.series, direction: card.config.sort.direction ?? 'desc' }
        : null;
  const activeSort = interactiveSort ?? configuredSort;
  const sorted = interactiveSort
    ? sortTableRows(shaped.rows, interactiveSort)
    : sortRows(shaped.rows, card.config?.sort, labelColumn2, numericColumns);
  const visible = sorted.slice(0, TABLE_VISIBLE_ROW_CAP);
  const summaryRow =
    (card.config?.summary === 'stats' || (!card.view?.x && !card.view?.series && isComparisonTable(shaped.columns))) &&
    sorted.length === 1 &&
    shaped.columns.length >= 2 &&
    shaped.columns.length <= 6 &&
    shaped.columns.every((column) => isNumeric(sorted[0][column]) && !shownAsText(column, sorted[0][column]));

  if (summaryRow) {
    return (
      <Box
        data-lens-table-content
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${shaped.columns.length}, minmax(0, 1fr))`,
          minHeight: 64,
        }}
      >
        {shaped.columns.map((column, index) => (
          <Box
            key={column}
            sx={{
              alignItems: 'flex-start',
              borderLeft: index === 0 ? undefined : '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 0,
              px: { xs: 1.25, sm: 2 },
            }}
          >
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.06em',
                lineHeight: 1.25,
                textTransform: 'uppercase',
              }}
            >
              {comparisonTableFieldLabel(column, card.config)}
            </Typography>
            <Typography
              data-lens-wand={wandTargetAttribute({
                kind: 'table-cell',
                label: comparisonTableFieldLabel(column, card.config),
                value: formatValue(sorted[0][column], comparisonTableFieldFormat(column, card.config)),
              })}
              sx={{
                fontFamily: LENS_HERO_FONT_FAMILY,
                fontSize: { xs: 20, md: 26 },
                fontVariantNumeric: 'tabular-nums',
                mt: 0.75,
              }}
            >
              {formatValue(sorted[0][column], comparisonTableFieldFormat(column, card.config))}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer
      data-lens-table-container
      sx={{
        // Fill the measured shell body and own the overflow here. Using the
        // plot estimate as a max-height let the table keep its natural height
        // when the grid frame was shorter, so it painted over the next card.
        height: '100%',
        maxHeight: '100%',
        overflow: 'auto',
        width: '100%',
      }}
    >
      <Table
        data-lens-table-content
        size="small"
        stickyHeader
        sx={{ tableLayout: 'auto', '& td': { overflowWrap: 'anywhere' }, '& th': { overflowWrap: 'normal' } }}
      >
        <TableHead>
          <TableRow>
            {shaped.columns.map((column) => {
              const isActive = activeSort?.column === column;
              const numeric = numericColumns.includes(column);
              return (
                <TableCell
                  align={numeric ? 'right' : 'left'}
                  key={column}
                  sortDirection={isActive ? activeSort.direction : false}
                  // A text column keeps a readable width; past it the table scrolls sideways instead of wrapping names a word per line.
                  sx={{ minWidth: numeric ? 128 : TEXT_COLUMN_MIN_WIDTH_PX, width: numeric ? '1%' : undefined }}
                >
                  <TableSortLabel
                    active={isActive}
                    direction={isActive ? activeSort.direction : 'asc'}
                    onClick={() => setInteractiveSort((current) => nextTableSort(current ?? configuredSort, column))}
                    sx={{
                      '&.Mui-active': { color: 'text.primary' },
                      '&.Mui-active .MuiTableSortLabel-icon': { color: 'primary.main' },
                      '& .MuiTableSortLabel-icon': { fontSize: 16 },
                      color: 'inherit',
                      fontWeight: 'inherit',
                    }}
                  >
                    {displayLensFieldName(column, card.config)}
                  </TableSortLabel>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((row, index) => {
            const filterColumn = labelColumn2;
            const value = String(row[filterColumn] ?? '');
            const rowSelectable = selectable && value.trim().length > 0;
            const active = Boolean(selection?.[filterColumn]?.includes(value));
            const activate = () => onSelect({ column: filterColumn, value });
            const rowLabelFormat = fieldFormat(card.config, labelColumn2);
            const rowLabel =
              mappedValueLabel(labelColumn2, row[labelColumn2]) ??
              (shownAsText(labelColumn2, row[labelColumn2])
                ? String(row[labelColumn2])
                : rowLabelFormat
                  ? formatValue(row[labelColumn2], rowLabelFormat)
                  : labelFormat
                    ? formatLensLabel(row[labelColumn2], labelFormat, calendar)
                    : (cellLabels[labelColumn2]?.(row[labelColumn2]) ?? formatLensLabel(row[labelColumn2])));
            return (
              <TableRow
                hover={rowSelectable}
                key={index}
                onClick={rowSelectable ? activate : undefined}
                sx={{ cursor: rowSelectable ? 'pointer' : undefined }}
                {...(rowSelectable ? crossFilterA11yProps(active, activate) : {})}
              >
                {shaped.columns.map((column) => {
                  const format = fieldFormat(card.config, column);
                  const numeric = numericColumns.includes(column);
                  return (
                    <TableCell
                      align={numeric ? 'right' : 'left'}
                      data-lens-wand={wandTargetAttribute({
                        kind: 'table-cell',
                        label:
                          rowLabel && column !== labelColumn2
                            ? `${rowLabel} · ${displayLensFieldName(column, card.config)}`
                            : displayLensFieldName(column, card.config),
                        value:
                          mappedValueLabel(column, row[column]) ??
                          (shownAsText(column, row[column])
                            ? String(row[column])
                            : formatValue(row[column], format, cellLabels[column])),
                      })}
                      key={column}
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: numeric ? 'nowrap' : 'normal',
                        width: numeric ? '1%' : undefined,
                      }}
                    >
                      {mappedValueLabel(column, row[column]) ??
                        (shownAsText(column, row[column]) && !isWebLink(row[column]) ? (
                          String(row[column])
                        ) : format?.kind === 'currency' ? (
                          <TableCurrencyValue format={format} value={row[column]} />
                        ) : isWebLink(row[column]) ? (
                          <Link
                            href={row[column]}
                            onClick={(event) => event.stopPropagation()}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {row[column]}
                          </Link>
                        ) : column === labelColumn2 && !format ? (
                          labelFormat ? (
                            formatLensLabel(row[column], labelFormat, calendar)
                          ) : (
                            (cellLabels[column]?.(row[column]) ?? formatLensLabel(row[column]))
                          )
                        ) : (
                          formatValue(row[column], format, cellLabels[column])
                        ))}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * A single row containing a current period, comparison period and change is
 * analytically a compact scorecard, even if it arrived from an older saved
 * table card before askRichard began saving explicit presentation metadata.
 */
function isComparisonTable(columns: Array<string>): boolean {
  return columns.some((column) => /(?:^|_)(?:yoy|mom|qoq|change|comparable|previous|prior)(?:_|$)/i.test(column));
}

function comparisonTableFieldLabel(column: string, config: LensCard['config']): string {
  const saved = config?.fieldLabels?.[column]?.trim();
  if (saved) {
    return saved;
  }
  if (/^yoy_change$/i.test(column)) {
    return 'YoY change';
  }
  if (/^mom_change$/i.test(column)) {
    return 'MoM change';
  }
  if (/^qoq_change$/i.test(column)) {
    return 'QoQ change';
  }
  if (/^change_/i.test(column)) {
    return `Change in ${column.slice('change_'.length).replace(/_/g, ' ')}`;
  }
  return displayLensFieldName(column, config);
}

function comparisonTableFieldFormat(column: string, config: LensCard['config']): LensValueFormat | undefined {
  // An old generic table card often has one card-wide decimal rule. It should
  // not defeat the semantic percentage and whole-number treatment below; an
  // explicitly saved per-field format always remains authoritative.
  const saved = config?.fieldFormats?.[column];
  if (saved) {
    return saved;
  }
  if (/(?:^|_)(?:yoy|mom|qoq)(?:_|$)/i.test(column)) {
    return { kind: 'percent', percentScale: 'unit', decimals: 1 };
  }
  return { kind: 'number', decimals: 0 };
}

const SPARKLINE_WIDTH = 120;
const SPARKLINE_HEIGHT = 34;
const SPARKLINE_PAD = 3;

/**
 * The trend behind a KPI figure at a glance: one Ocean Teal line, the latest
 * point marked in Coral Green, no axes. It shows shape, not values; the
 * figure beside it is the number, and the sibling chart is where a reader
 * goes for the detail.
 */
function formatMetricFigure(value: number, format?: LensValueFormat): string {
  const plain = !format?.prefix && !format?.suffix && !format?.decimals;
  const magnitude = Math.abs(value);
  if (plain && format?.kind === 'currency' && format.currency && magnitude >= 100_000) {
    return new Intl.NumberFormat(format.locale || 'en-AU', {
      currency: format.currency,
      maximumFractionDigits: 2,
      notation: 'compact',
      style: 'currency',
    }).format(value);
  }
  if (plain && (!format?.kind || format.kind === 'number') && magnitude >= 1_000_000) {
    return new Intl.NumberFormat(format?.locale || 'en-AU', { maximumFractionDigits: 2, notation: 'compact' }).format(
      value,
    );
  }
  return formatLensValue(value, format);
}

function MetricSparkline({ theme, trend }: { theme: Theme; trend: MetricTrend }) {
  const tokens = lensTokens(theme);
  const { values } = trend;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const innerWidth = SPARKLINE_WIDTH - SPARKLINE_PAD * 2;
  const innerHeight = SPARKLINE_HEIGHT - SPARKLINE_PAD * 2;
  const step = values.length > 1 ? innerWidth / (values.length - 1) : 0;
  const points = values.map((value, index) => ({
    x: SPARKLINE_PAD + index * step,
    y: SPARKLINE_PAD + innerHeight - ((value - min) / span) * innerHeight,
  }));
  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)},${point.y.toFixed(1)}`)
    .join(' ');
  const last = points[points.length - 1];
  const first = trend.dates[0];
  const latest = trend.dates[trend.dates.length - 1];
  return (
    <Box
      aria-label={`Trend by ${trend.dateColumn}, ${first} to ${latest}, ${values.length} points`}
      component="svg"
      data-testid="metric-sparkline"
      height={SPARKLINE_HEIGHT}
      role="img"
      sx={{
        display: 'block',
        flex: 'none',
        mt: 0.75,
        // The figure is the answer. On a compact card, the trend is useful
        // only if it can sit beside the figure without forcing it to paint
        // over the line. Container queries follow the actual card frame, not
        // a misleading whole-window breakpoint (the workspace rail is often
        // open), so resizing always produces the same readable composition.
        '@container (max-width: 320px)': { display: 'none' },
      }}
      viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
      width={SPARKLINE_WIDTH}
    >
      <path d={path} fill="none" stroke={tokens.accent} strokeLinejoin="round" strokeWidth={1.6} />
      <circle cx={last.x} cy={last.y} fill={BRAND_COLORS.coralGreen} r={2.6} stroke={tokens.accent} strokeWidth={1} />
    </Box>
  );
}

function MetricComparison({
  current,
  format,
  metricAlignment,
  comparison,
  note,
  partial = false,
  pinned = false,
  tight = false,
}: {
  current: number;
  format?: LensValueFormat;
  metricAlignment: LensCardAlignment;
  comparison?: { isFetching: boolean; label: string; result: ReturnType<typeof resolveMetricHeadline> | null };
  note?: string;
  partial?: boolean;
  /** A pinned window names what it compares against; a tile on the board's window leaves that to the chip. */
  pinned?: boolean;
  tight?: boolean;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const noteLine = note ? (
    <Typography
      component="div"
      data-lens-pdf-comparison="neutral"
      sx={{ color: 'text.secondary', lineHeight: 1.25, textAlign: metricAlignment }}
      variant="caption"
    >
      <span data-lens-pdf-comparison-detail="">{note}</span>
    </Typography>
  ) : null;
  if (!comparison) {
    return noteLine;
  }
  if (comparison.isFetching) {
    return (
      <Typography sx={{ color: tokens.accent, textAlign: metricAlignment }} variant="caption">
        Loading comparison…
      </Typography>
    );
  }
  if (comparison.result?.kind !== 'value') {
    return noteLine;
  }
  const movement = formatMetricMovement(current, comparison.result.value, format);
  if (!movement) {
    return noteLine;
  }
  const against = partial ? 'partial' : pinned ? `vs ${compactMetricComparisonLabel(comparison.label)}` : '';
  const suffix = note && !(partial && note.includes('partial')) ? (against ? ` · ${note}` : note) : '';
  const tone = metricMovementTone(current, comparison.result.value);
  const movementColor =
    tone === 'positive'
      ? theme.palette.success.main
      : tone === 'negative'
        ? theme.palette.error.main
        : theme.palette.text.secondary;
  const comparisonTarget = wandTargetAttribute({
    kind: 'comparison',
    label: comparison.label,
    value: `${movement} vs ${comparison.label}`,
  });
  if (tight) {
    return (
      <Typography
        component="div"
        data-lens-pdf-comparison={tone}
        data-lens-wand={comparisonTarget}
        title={`${movement} vs ${comparison.label}`}
        sx={{
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.25,
          overflow: 'hidden',
          textAlign: metricAlignment,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
        variant="caption"
      >
        <Box component="span" data-lens-pdf-movement="" sx={{ color: movementColor, fontWeight: 600 }}>
          {movement}
        </Box>
        {(against || suffix) && (
          <Box component="span" data-lens-pdf-comparison-detail="" sx={{ color: 'text.secondary' }}>
            {' '}
            {against}
            {suffix}
          </Box>
        )}
      </Typography>
    );
  }
  return (
    <Typography
      component="div"
      data-lens-pdf-comparison={tone}
      data-lens-wand={comparisonTarget}
      sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.25, textAlign: metricAlignment }}
      variant="caption"
    >
      <Box component="span" data-lens-pdf-movement="" sx={{ color: movementColor, display: 'block', fontWeight: 600 }}>
        {movement}
      </Box>
      {(against || suffix) && (
        <Box component="span" data-lens-pdf-comparison-detail="" sx={{ color: 'text.secondary', display: 'block' }}>
          {against}
          {suffix}
        </Box>
      )}
    </Typography>
  );
}

/**
 * A card's reading, as quiet supporting text under its answer.
 *
 * A refusal prints its reason rather than nothing. One bare card among several
 * reads as a card that failed, where a stated reason ("the result was capped")
 * is both honest and the more useful fact.
 */
function LensCardReading({
  cardTitle,
  onOpenSettings,
  reading,
  prominent = false,
}: {
  cardTitle?: string;
  onOpenSettings?: () => void;
  reading: ComputedReading;
  prominent?: boolean;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const refused = reading.kind === 'refused';
  const text = (
    <Typography
      component="p"
      data-lens-wand={wandTargetAttribute({
        kind: 'text',
        label: cardTitle ? `${cardTitle} commentary` : 'commentary',
        value: refused ? reading.reason : reading.text,
      })}
      sx={{
        color: refused ? theme.palette.text.secondary : theme.palette.text.primary,
        fontSize: prominent ? 14 : undefined,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: prominent ? 1.55 : 1.45,
        m: 0,
        textAlign: 'left',
      }}
      variant="caption"
    >
      {refused ? reading.reason : reading.text}
    </Typography>
  );
  if (prominent) {
    return text;
  }
  const content = (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="flex-start"
      spacing={0.75}
      sx={{
        // Reserve the same two-line rhythm for neighbouring inline readings,
        // while allowing longer explanations to keep wrapping naturally.
        minHeight: '2.9em',
        width: '100%',
      }}
    >
      <Box
        aria-hidden
        sx={{ backgroundColor: tokens.accent, borderRadius: '50%', flex: 'none', height: 6, width: 6 }}
      />
      {text}
    </Stack>
  );
  if (!onOpenSettings) {
    return content;
  }
  return (
    <ButtonBase
      aria-label={`Edit commentary${cardTitle ? ` for ${cardTitle}` : ''}`}
      onClick={(event) => {
        event.stopPropagation();
        onOpenSettings();
      }}
      sx={{
        borderRadius: 0.5,
        display: 'flex',
        textAlign: 'left',
        width: '100%',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {content}
    </ButtonBase>
  );
}

function useLensInsightAnswer(
  question: string,
  persistedInsight: LensInsight | undefined,
  refresh: number,
  onPersistInsight: ((insight: LensInsight) => Promise<void>) | undefined,
  lensId?: string,
  boardEvidence = '',
  isFetchingData = false,
  viewScope?: LensInsightViewScope,
  drafting = false,
) {
  const [answer, setAnswer] = useState(() => persistedInsight?.content ?? '');
  const [status, setStatus] = useState('');
  const [isWorking, setIsWorking] = useState(() => !persistedInsight?.content);
  const [refreshedAt, setRefreshedAt] = useState(() => persistedInsight?.refreshedAt);
  const previousQuestionRef = useRef(question);
  const completedRefreshRef = useRef(0);
  // A completed stream updates the parent Lens, which re-renders this card
  // with the saved answer. Re-running the effect for that ordinary prop update
  // would immediately start another LLM call, so read the current value from a
  // ref while only question changes and explicit refreshes start new work.
  const persistedInsightRef = useRef(persistedInsight);
  persistedInsightRef.current = persistedInsight;
  const persistInsightRef = useRef(onPersistInsight);
  persistInsightRef.current = onPersistInsight;
  const boardEvidenceRef = useRef(boardEvidence);
  boardEvidenceRef.current = boardEvidence;
  const viewScopeRef = useRef(viewScope);
  viewScopeRef.current = viewScope;
  const viewScopeKey = JSON.stringify(viewScope);
  // The insight's own background session can make `drafting` true. Once the
  // readings are settled, that flag must not abort the answer it just started.
  const waitingForBoard = isFetchingData || (drafting && lensInsightHasPendingReadings(boardEvidence));
  const waitingForBoardRef = useRef(waitingForBoard);
  waitingForBoardRef.current = waitingForBoard;

  useEffect(() => {
    let active = true;
    const questionChanged = previousQuestionRef.current !== question;
    const savedContent = persistedInsightRef.current?.content?.trim();
    const reuseSaved = refresh === completedRefreshRef.current && !questionChanged && savedContent;

    // A Lens read is only a read. Existing saved text must appear immediately
    // and must not spend another AskRichard turn merely because the card
    // mounted (or its parent refetched after saving a different card).
    if (reuseSaved) {
      setAnswer(savedContent);
      setStatus('');
      setIsWorking(false);
      setRefreshedAt(persistedInsightRef.current?.refreshedAt);
      return () => {
        active = false;
      };
    }

    if (waitingForBoard) {
      setStatus('Waiting for the Lens readings…');
      setIsWorking(true);
      return () => {
        active = false;
      };
    }

    // A new question or a manual refresh must not leave the previous turn
    // running: without this, both an unmount and a refresh mid-stream leave
    // the request live against the AskRichard/LLM backend after the UI has
    // stopped listening to it.
    const controller = new AbortController();
    // During a deliberate refresh retain the last saved answer until the new
    // one has arrived. Clearing it made the card flash through a skeleton and
    // made the reader lose the useful answer while merely asking for an update.
    if (questionChanged || refresh === 0) {
      setAnswer('');
    }
    setStatus(refresh > 0 && !questionChanged ? 'Refreshing insight…' : 'askRichard is analysing…');
    setIsWorking(true);
    let streamedAnswer = '';
    let receivedAnswer = false;
    let invalidAnswer = false;
    const requestedEvidence = boardEvidenceRef.current;
    const requestedViewScope = viewScopeRef.current;
    const requestedScope = requestedViewScope?.effectiveFilters
      ? { effectiveFilters: requestedViewScope.effectiveFilters, ranges: requestedViewScope.ranges }
      : undefined;
    const requestedScopeKey = JSON.stringify(requestedViewScope);
    const preferNewerSavedInsight = () => {
      const newer = newerAutomaticLensInsight(persistedInsightRef.current, savedContent, refresh);
      if (!newer) {
        return false;
      }
      setAnswer(newer.content);
      setRefreshedAt(newer.refreshedAt);
      previousQuestionRef.current = question;
      setStatus('');
      setIsWorking(false);
      return true;
    };
    const finish = () => {
      if (!active) {
        return;
      }
      if (preferNewerSavedInsight()) {
        return;
      }
      if (
        waitingForBoardRef.current ||
        boardEvidenceRef.current !== requestedEvidence ||
        JSON.stringify(viewScopeRef.current) !== requestedScopeKey
      ) {
        setAnswer(persistedInsightRef.current?.content ?? '');
        setStatus('Waiting for the Lens readings…');
        setIsWorking(true);
        return;
      }
      const content = streamedAnswer.trim();
      if (!content || invalidAnswer) {
        setAnswer(persistedInsightRef.current?.content ?? '');
        setStatus('askRichard could not complete this insight. Refresh to try again.');
        setIsWorking(false);
        return;
      }
      const persist = persistInsightRef.current;
      if (!persist) {
        completedRefreshRef.current = refresh;
        previousQuestionRef.current = question;
        setStatus('');
        setIsWorking(false);
        return;
      }
      setStatus('Saving insight…');
      const savedInsight = {
        content,
        refreshedAt: new Date().toISOString(),
        ...(requestedScope ? { scope: requestedScope } : {}),
      };
      void persist(savedInsight)
        .then(() => {
          if (active) {
            completedRefreshRef.current = refresh;
            previousQuestionRef.current = question;
            setRefreshedAt(savedInsight.refreshedAt);
            setStatus('');
            setIsWorking(false);
          }
        })
        .catch(() => {
          if (active) {
            setStatus('This refresh could not be saved. Refresh to try again.');
            setIsWorking(false);
          }
        });
    };
    // The question is the only text the session stores; the instruction and the board readings
    // travel as context blocks, so the chat pane never shows them as the person's words.
    const askRequest = lensInsightAskRequest(question, requestedEvidence, requestedViewScope);
    void streamReportingAiAsk(
      {
        question: askRequest.question,
        context: askRequest.context,
        engine: 'mantle',
        humanInitiated: false,
        purpose: lensId ? 'lens_insight' : undefined,
        origin: lensId ? 'lens_chat' : undefined,
        lensId,
      },
      {
        onProgress: (message) => {
          if (active && message && !preferNewerSavedInsight()) {
            setStatus(message);
          }
        },
        onDelta: (chunk) => {
          if (active) {
            if (preferNewerSavedInsight()) {
              return;
            }
            streamedAnswer += chunk;
            if (!receivedAnswer) {
              receivedAnswer = true;
              setAnswer(chunk);
            } else {
              setAnswer((current) => current + chunk);
            }
          }
        },
        onContent: (block) => {
          if (active && preferNewerSavedInsight()) {
            return;
          }
          // A proposal's closing acknowledgement is not an analytical answer.
          // Never auto-apply the proposal or save its acknowledgement as prose.
          if (block.type.startsWith('lens_') || block.type === 'error') {
            invalidAnswer = true;
            return;
          }
          if (block.type !== 'text') {
            return;
          }
          if (block.payload?.section === 'thought-process') {
            return;
          }
          const content = block.payload?.content ?? block.payload?.markdown ?? block.payload?.body;
          if (active && typeof content === 'string' && content.trim()) {
            streamedAnswer = content;
            receivedAnswer = true;
            setAnswer(content);
          }
        },
        onDone: finish,
        onStreamError: (message) => {
          invalidAnswer = true;
          if (active) {
            if (preferNewerSavedInsight()) {
              return;
            }
            setAnswer(persistedInsightRef.current?.content ?? '');
            setStatus(message);
            setIsWorking(false);
          }
        },
      },
      { signal: controller.signal },
    ).catch(() => {
      if (active) {
        if (preferNewerSavedInsight()) {
          return;
        }
        setAnswer(persistedInsightRef.current?.content ?? '');
        setStatus('askRichard could not complete this insight. Refresh to try again.');
        setIsWorking(false);
      }
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [question, refresh, lensId, waitingForBoard, boardEvidence, viewScopeKey]);

  return { answer, isWorking, refreshedAt, status };
}

function LensInsightCardView({
  card,
  savedInsightScopeChanged = false,
  insightScope,
  duplicateOf,
  canEdit,
  editorMode = true,
  showActions,
  lensId,
  boardEvidence,
  drafting = false,
  dateRange,
  boardFilters = [],
  isFetchingData,
  onAskRichard,
  onCardSelect,
  isSelected,
  highlightedField,
  selectionMode,
  isBulkSelected,
  onToggleBulkSelection,
  onDelete,
  onDuplicate,
  onMoveToSection,
  onEdit,
  onRefresh,
  onPersistInsight,
  isRefreshing,
  insightRefresh,
  span,
  canvasLayout,
  canvasNodeRef,
  canvasStyle,
  dragHandle,
}: LensCardViewProps) {
  const theme = useTheme();
  const height = cardHeight(card.size, card.height);
  const size = card.size ?? DEFAULT_SIZE;
  const isCompact = size === 'small' || size === 'medium';
  const question = card.insightQuestion ?? card.description ?? card.title;
  const { answer, isWorking, refreshedAt, status } = useLensInsightAnswer(
    question,
    card.insight,
    insightRefresh,
    onPersistInsight,
    lensId,
    boardEvidence,
    isFetchingData,
    {
      period: dateRange,
      filters: boardFilters.map(({ label, column, filterId }) => ({ label, column, filterId })),
      effectiveFilters: insightScope?.effectiveFilters,
      ranges: insightScope?.ranges,
    },
    drafting,
  );

  // An insight is generated text, not a query result: 'loading' while nothing
  // has come back yet, 'error' if the stream ended without an answer, and
  // 'ready' the moment there is text on screen, even while more streams in.
  const state: LensCardState = answer ? 'ready' : isWorking ? 'loading' : 'error';
  const scopeNotice = lensInsightScopeNotice(card.insight?.scope, insightScope, savedInsightScopeChanged);

  return (
    <Box
      onClickCapture={(event) => {
        // React portal events still reach this owner; menu/dialog actions are outside the card body.
        if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target)) return;
        const control =
          event.target instanceof Element
            ? event.target.closest<HTMLElement>(
                'button, a, input, textarea, [data-lens-card-description], [role="button"], .react-resizable-handle',
              )
            : null;
        if (control && control !== event.currentTarget) {
          return;
        }
        if (selectionMode) {
          onToggleBulkSelection?.();
          return;
        }
        onCardSelect?.();
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (!selectionMode && !onCardSelect) {
          return;
        }
        if ((event.key === 'Enter' || event.key === ' ') && !event.nativeEvent.isComposing) {
          event.preventDefault();
          if (selectionMode) {
            onToggleBulkSelection?.();
          } else {
            onCardSelect?.();
          }
        }
      }}
      ref={canvasNodeRef}
      {...(selectionMode || onCardSelect
        ? {
            'aria-label': selectionMode ? `Select ${card.title}` : `Use ${card.title} in askRichard`,
            role: 'button' as const,
            tabIndex: 0,
          }
        : {})}
      style={canvasStyle}
      sx={
        canvasLayout
          ? {
              ...(isSelected || isBulkSelected || highlightedField
                ? { '& > .MuiCard-root': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }
                : {}),
              // The grid engine positions and sizes the frame; the card fills
              // it, which is what makes a resize change the card and not just
              // the space around it.
              height: '100%',
              minWidth: 0,
            }
          : cardResponsiveSx(span, height.card)
      }
    >
      <LensCardShell
        bodyHeight={height.body}
        bodyAlign="top"
        canEdit={canEdit}
        cardMinHeight={canvasLayout ? 0 : height.card}
        compact={false}
        titleAlignment={card.config?.titleAlignment}
        titleSize={card.config?.titleSize}
        fillFrame={Boolean(canvasLayout)}
        duplicateOf={duplicateOf}
        description={highlightedField ? `Uses ${highlightedField}` : card.description}
        dragHandle={dragHandle}
        error={{ message: status || 'askRichard could not complete this insight.', onRetry: onRefresh }}
        footer={{
          source: 'askRichard analysis',
          freshness: isWorking ? status || 'Generating' : status || formatCardFreshness(refreshedAt),
        }}
        icon={<LightbulbOutlinedIcon color="primary" />}
        loadingKind="insight"
        loadingContent={<AskRichardWorking label={status || 'askRichard is analysing…'} />}
        onDelete={() => void onDelete()}
        onDuplicate={onDuplicate ? () => void onDuplicate() : undefined}
        onMoveToSection={onMoveToSection}
        onEdit={onEdit}
        onAskRichard={onAskRichard}
        onRefresh={onRefresh}
        refreshLabel="Refresh insight"
        // isRefreshing tracks the card-data refresh mutation, which an insight
        // card's onRefresh never calls: it just bumps insightRefresh. Fold in
        // isWorking too, or the control stays enabled through the whole
        // AskRichard turn and rapid clicks stack concurrent, uncancelled runs.
        refreshing={isRefreshing || isWorking}
        selectionControl={
          selectionMode && onToggleBulkSelection
            ? { checked: Boolean(isBulkSelected), label: `Select ${card.title}`, onChange: onToggleBulkSelection }
            : undefined
        }
        showDescription={!isCompact}
        showActions={showActions ?? editorMode}
        showFooter={false}
        state={state}
        title={card.title}
      >
        {state === 'ready' && (
          <Stack data-lens-insight-content spacing={1} sx={{ height: '100%', minHeight: 0, width: '100%' }}>
            {scopeNotice && !isWorking && card.insight?.content?.trim() === answer && (
              <Typography variant="body2" color="text.secondary">
                {scopeNotice}
              </Typography>
            )}
            <LeDataMarkdownPreview
              boxProps={{
                sx: {
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  textAlign: 'left',
                  width: '100%',
                  '& p:first-of-type': { mt: 0 },
                  '& p:last-child': { mb: 0 },
                },
              }}
              content={answer}
            />
            {isWorking && <AskRichardWorking label={status || 'Still writing…'} />}
          </Stack>
        )}
      </LensCardShell>
    </Box>
  );
}

/** Axis labels stay compact so a currency series does not crowd the plot. */
function axisFormatter(format?: LensValueFormat) {
  return (value: unknown) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '';
    }
    if (format?.kind === 'percent') {
      return formatLensValue(numeric, format);
    }
    const options: Intl.NumberFormatOptions = { notation: 'compact', maximumFractionDigits: 1 };
    if (format?.kind === 'currency' && format.currency) {
      options.style = 'currency';
      options.currency = format.currency;
    }
    return new Intl.NumberFormat(format?.locale || 'en-AU', options).format(numeric);
  };
}

// Exported so a table cell's fallback formatting can be checked directly,
// without standing up the whole card render tree for a one-line rule.
export function formatValue(
  value: unknown,
  format?: LensValueFormat,
  label: (value: unknown) => string | undefined = valueLabel,
): string {
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    return JSON.stringify(value);
  }
  // A BigQuery NUMERIC or BIGNUMERIC column arrives as a string to avoid
  // losing precision, so typeof alone would print it raw beside a formatted
  // column drawing from the same query.
  return isNumeric(value) || format ? formatLensValue(value, format) : (label(value) ?? String(value ?? NONE_LABEL));
}
